import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import React, { useEffect, useState, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import '../../styles/calendar.css';
import ProfileBanner from './Banner';
import TeamVacationsCalendar from './TeamVacationsCalendar';
import VacationSection from './VacationSection';

const compressImage = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                // Limitamos la dimensión para garantizar que quede en unos ~50kb
                const MAX_WIDTH = 300;
                const MAX_HEIGHT = 300;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = "#FFFFFF"; // Fondo blanco para PNGs transparentes
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                // Exportar como JPEG al 70% de calidad
                canvas.toBlob((blob) => {
                    if (!blob) {
                        reject(new Error('Canvas is empty'));
                        return;
                    }
                    const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    resolve(compressedFile);
                }, 'image/jpeg', 0.7);
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};

const Profile = () => {
    const { id } = useParams();
    const { showErrorToast, showSuccessToast } = useToast();
    const { token, setToken, idUsuario, idCatTipoUsuario, setProfileImage } = useToken();

    const [cargando, setCargando] = useState(true);

    const [perfil, setPerfil] = useState([]);
    const [imagenPerfil, setImagenPerfil] = useState("");
    const [refreshVacations, setRefreshVacations] = useState(0);
    const fileInputRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [pendingUploadFile, setPendingUploadFile] = useState(null);

    // Estado compartido para conocer los días disponibles de forma reactiva
    const [diasDisponibles, setDiasDisponibles] = useState(0);
    const [savingVacation, setSavingVacation] = useState(false);

    const obtenerPerfil = async () => {
        try {
            const idDestino = !id ? idUsuario : id;
            const response = await fetch("/api/perfil/obtenerDatosPerfil/?id=" + idDestino, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType == 1) {
                    const detalles = JSON.parse(data.detalles);
                    const datosUsuario = detalles['table2'][0];
                    setPerfil(datosUsuario);

                    const resImagen = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${idDestino}`, {
                        method: 'GET',
                        headers: { 'Authorization': 'Bearer ' + token }
                    });

                    if (resImagen.ok) {
                        const imagenBase64 = await resImagen.text();

                        if (imagenBase64 && imagenBase64 !== "data:image/jpg;base64,") {
                            setImagenPerfil(imagenBase64);

                            if (!id || id === idUsuario) {
                                localStorage.setItem(`perfil_${idUsuario}`, imagenBase64);
                                if (typeof setProfileImage === 'function') {
                                    setProfileImage(imagenBase64);
                                }
                            }
                        } else {
                            setImagenPerfil("/img/default-avatar.png");
                        }
                    }

                    // Disparar cálculo de vacaciones
                    if (datosUsuario?.idUsuario) {
                        await cargarDiasDisponibles(datosUsuario.idUsuario);
                    }
                } else {
                    showErrorToast(data.vMessage);
                }
            } else if (response.status == 401) {
                setToken(null);
                window.location.reload();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setCargando(false);
        }
    };

    const cargarDiasDisponibles = async (userId) => {
        try {
            const response = await fetch(`/api/vacaciones/CalcularDiasVacacionesLFT?idUsuario=${userId}`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
            });
            const data = await response.json();
            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const resumen = detalles.table2?.[0] || detalles.Table2?.[0];
                setDiasDisponibles(resumen ? resumen.iDiasDisponibles : 0);
            }
        } catch (error) {
            console.error("Error al calcular días:", error);
        }
    };

    // Nueva función controladora enviada al calendario
    const handleDirectVacationRequest = async (fechasSeleccionadas, motivo = '') => {
        setSavingVacation(true);
        try {
            const requestBody = {
                solicitud: {
                    idUsuario: perfil.idUsuario.toString(),
                    fechasSolicitadas: fechasSeleccionadas,
                    vMotivoSolicitud: motivo ? motivo.trim() : ""

                }
            };

            const response = await fetch("/api/vacaciones/SolicitarVacaciones", {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast(data.vMessage || 'Solicitud de vacaciones enviada (Estado: Pendiente)');
                await cargarDiasDisponibles(perfil.idUsuario); // Recalcular días
                setRefreshVacations(prev => prev + 1); // Forzar refresco visual
                return true;
            } else {
                throw new Error(data.vMessage || 'Error al procesar la solicitud');
            }
        } catch (error) {
            showErrorToast(error.message);
            return false;
        } finally {
            setSavingVacation(false);
        }
    };

    const handleVacationUpdate = async () => {
        if (perfil?.idUsuario) await cargarDiasDisponibles(perfil.idUsuario);
        setRefreshVacations(prev => prev + 1);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Limitar subida a 1 MB
        if (file.size > 1024 * 1024) {
            showErrorToast("El archivo es demasiado grande. El máximo permitido es 1 MB.");
            e.target.value = ''; // Limpiar el input
            return;
        }

        if (!["image/jpeg", "image/png"].includes(file.type)) {
            showErrorToast("Solo se permiten archivos JPG y PNG");
            e.target.value = '';
            return;
        }

        try {
            // Comprimir la imagen antes de subirla
            const compressedFile = await compressImage(file);
            const previewUrl = URL.createObjectURL(compressedFile);
            setPreviewImage(previewUrl);
            setPendingUploadFile(compressedFile);
        } catch (error) {
            console.error("Error al comprimir la imagen", error);
            showErrorToast("Error al procesar la imagen.");
        } finally {
            e.target.value = '';
        }
    };

    const handleSavePhoto = async () => {
        if (!pendingUploadFile) return;

        var data = new FormData();
        data.append('FormFile', pendingUploadFile);

        try {
            const response = await fetch("/api/perfil/CargarFoto", {
                method: 'POST',
                body: data,
                headers: {
                    'Authorization': 'Bearer ' + token
                }
            }).catch((error) => {
                showErrorToast('Hubo un problema con la petición Fetch:' + error);
                console.error('Hubo un problema con la petición Fetch:' + error);
            });

            if (response && response.ok) {
                const resData = await response.json();

                if (resData.iResponseType == 1) {
                    const imagenBase64 = resData.detalles;

                    if (typeof setProfileImage === 'function') {
                        setProfileImage(imagenBase64);
                    }

                    localStorage.setItem(`perfil_${idUsuario}`, imagenBase64);

                    if (typeof setImagenPerfil === 'function') {
                        setImagenPerfil(imagenBase64);
                    }

                    obtenerPerfil();
                    showSuccessToast("Foto subida correctamente");
                    setPreviewImage(null);
                    setPendingUploadFile(null);
                } else {
                    showErrorToast(resData.vMessage);
                }
            } else if (response) {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status == 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            console.error("Error al subir la imagen", error);
            showErrorToast("Error al procesar la imagen.");
        }
    };

    const handleCancelPhoto = () => {
        if (previewImage) {
            URL.revokeObjectURL(previewImage);
        }
        setPreviewImage(null);
        setPendingUploadFile(null);
    };
    useEffect(() => {
        obtenerPerfil();
    }, [id]);

    return (
        <>
            {cargando ? (
                <h5><Spinner animation="border" variant="primary" className="me-2" /> Cargando Perfil...</h5>
            ) : (
                <>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/png, image/jpeg"
                        onChange={handleFileChange}
                    />
                    <ProfileBanner
                        perfil={perfil}
                        onAvatarClick={() => {
                            if (!pendingUploadFile && fileInputRef.current) {
                                fileInputRef.current.click();
                            }
                        }}
                        imagenPerfil={previewImage || imagenPerfil}
                        onVacationUpdate={handleVacationUpdate}
                        isPendingUpload={!!pendingUploadFile}
                        onSavePhoto={handleSavePhoto}
                        onCancelPhoto={handleCancelPhoto}
                    />
                    <div className="mb-4">
                        <TeamVacationsCalendar
                            diasDisponibles={diasDisponibles}
                            onSelectionSubmit={handleDirectVacationRequest}
                            saving={savingVacation}
                            idUsuarioSeleccionado={perfil?.idUsuario}
                        />
                    </div>

                    {(!id || id === idUsuario.toString() || idCatTipoUsuario == "1") && (
                        <VacationSection
                            perfil={perfil}
                            refreshTrigger={refreshVacations}
                        />
                    )}

                </>
            )}
        </>
    );
};



export default Profile;